import { Ellipsis } from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { cn } from '../lib/cn.ts';
import { useT } from '../lib/i18n.tsx';
import { useMe } from '../lib/queries.ts';
import { carryOver } from '../lib/url-state.ts';
import { NAV_ITEMS, type NavItem } from './nav.ts';

/** More tabs than this do not fit a phone with readable labels: the rest go behind "More". */
const MAX_TABS = 5;

const tabClass =
  'flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-lg px-1 text-xs font-medium text-muted-foreground transition-colors active:bg-muted';

/** The pages shown as tabs; with too many, the last tab becomes "More" (opens the full menu). */
export function splitTabs(items: readonly NavItem[]): { tabs: NavItem[]; overflow: boolean } {
  if (items.length <= MAX_TABS) return { tabs: [...items], overflow: false };
  return { tabs: items.slice(0, MAX_TABS - 1), overflow: true };
}

/**
 * Phone and tablet navigation, like a mobile app: the main pages one tap away at the bottom of
 * the screen, above the iPhone home indicator. Hidden on large screens, which have the sidebar.
 */
export function BottomNav({ onMore }: { onMore: () => void }) {
  const t = useT();
  const me = useMe();
  const { pathname, search } = useLocation();
  const allowed = NAV_ITEMS.filter((item) => me.data?.permissions.includes(item.permission));
  const { tabs, overflow } = splitTabs(allowed);
  if (allowed.length === 0) return null;
  // "More" is the active tab while the page shown is one of those behind it.
  const moreActive =
    overflow &&
    !tabs.some((item) => (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)));

  return (
    <nav
      aria-label={t.nav.bottomLabel}
      className="flex shrink-0 gap-1 border-t bg-card px-1 pt-1 pb-[max(0.25rem,env(safe-area-inset-bottom))] lg:hidden"
    >
      {tabs.map((item) => (
        <NavLink
          key={item.to}
          to={{ pathname: item.to, search: carryOver(new URLSearchParams(search)) }}
          end={item.to === '/'}
          className={({ isActive }) => cn(tabClass, isActive && 'bg-accent text-accent-foreground')}
        >
          <item.icon aria-hidden="true" className="size-6 shrink-0" />
          <span className="max-w-full truncate">{t.nav.short[item.key]}</span>
        </NavLink>
      ))}
      {overflow ? (
        <button
          type="button"
          onClick={onMore}
          className={cn(tabClass, moreActive && 'bg-accent text-accent-foreground')}
        >
          <Ellipsis aria-hidden="true" className="size-6 shrink-0" />
          <span>{t.nav.more}</span>
        </button>
      ) : null}
    </nav>
  );
}
